/**
 * MdmAccessDeniedPage — shown on the optiedge variant when a user without
 * MDM admin permissions reaches `/app/mdm/**`.
 *
 * The optiedge product is MDM-only — there's no signage app to fall back to —
 * so we render a clear "contact your administrator" page instead of redirecting.
 * On the optisigns variant, `MdmFeatureRoute` redirects to `/app/screenManagement`
 * instead and never mounts this page.
 *
 * Spec 19 admin-only model — Phase A. Owner / Super Admin only.
 */
import { useTranslation } from 'react-i18next';
import { ShieldAlert } from 'lucide-react';
import { Button } from '@scio3/ui';
import { useAuthStore } from '@/stores/auth-store';
import { auth, signOut } from '@/lib/firebase';

export default function MdmAccessDeniedPage() {
  const { t } = useTranslation();
  const me = useAuthStore((s) => s.me);

  return (
    <div
      className="flex min-h-[80vh] items-center justify-center p-6"
      data-testid="mdm-access-denied-page"
    >
      <div className="flex max-w-md flex-col items-center gap-4 rounded-lg border bg-card p-6 text-center shadow-sm">
        <div className="rounded-full bg-destructive/10 p-3 text-destructive">
          <ShieldAlert className="h-6 w-6" aria-hidden />
        </div>
        <div className="space-y-2">
          <h1 className="text-lg font-semibold">{t('MDM access requires admin privileges')}</h1>
          <p className="text-sm text-muted-foreground">
            {t(
              "OptiEdge MDM is restricted to your account's Owner and Super Admin users. Ask your administrator to grant you access or invite you with the right role.",
            )}
          </p>
          {me?.email && (
            <p className="text-xs text-muted-foreground">
              {t('Signed in as')} <span className="font-medium">{me.email}</span>
            </p>
          )}
        </div>
        <Button
          variant="outline"
          onClick={() => {
            void signOut(auth);
          }}
          className="cursor-pointer"
          data-testid="mdm-access-denied-sign-out"
        >
          {t('Sign out')}
        </Button>
      </div>
    </div>
  );
}
